import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  Award, AlertTriangle, CheckCircle, ArrowLeft, Plus, Calendar, Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CertificationsPage = () => {
  const [certifications, setCertifications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: "",
    certification_name: "",
    issuing_authority: "",
    issue_date: "",
    expiry_date: ""
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [certsRes, empRes] = await Promise.all([
          axios.get(`${API}/admin/training/certifications`, { headers }),
          axios.get(`${API}/admin/employees`, { headers })
        ]);
        setCertifications(certsRes.data.certifications || []);
        setEmployees(empRes.data.employees || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/training/certifications`, formData, { headers });
      toast.success("Certification added");
      setShowForm(false);
      const res = await axios.get(`${API}/admin/training/certifications`, { headers });
      setCertifications(res.data.certifications || []);
    } catch (error) {
      toast.error("Failed to add certification");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "valid": return "bg-green-500/20 text-green-400";
      case "expiring_soon": return "bg-yellow-500/20 text-yellow-400";
      case "expired": return "bg-red-500/20 text-red-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  const validCerts = certifications.filter(c => c.expiry_status === "valid").length;
  const expiringCerts = certifications.filter(c => c.expiry_status === "expiring_soon").length;
  const expiredCerts = certifications.filter(c => c.expiry_status === "expired").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Certifications...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="certifications-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Certifications
            </h1>
            <p className="text-slate-400 text-sm">Track employee professional certifications</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Certification
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <Award className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{certifications.length}</p>
            <p className="text-xs text-slate-400">Total Certifications</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{validCerts}</p>
            <p className="text-xs text-slate-400">Valid</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <AlertTriangle className="w-6 h-6 text-yellow-400 mb-2" />
            <p className="text-2xl font-bold text-yellow-400">{expiringCerts}</p>
            <p className="text-xs text-slate-400">Expiring Soon</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Shield className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{expiredCerts}</p>
            <p className="text-xs text-slate-400">Expired</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {(expiringCerts > 0 || expiredCerts > 0) && (
        <div className="mb-6 space-y-2">
          {expiredCerts > 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{expiredCerts} certifications have expired and need renewal</span>
            </div>
          )}
          {expiringCerts > 0 && (
            <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400">{expiringCerts} certifications expiring within 30 days</span>
            </div>
          )}
        </div>
      )}

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">Add Certification</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Employee</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Certification Name</label>
                  <Input
                    value={formData.certification_name}
                    onChange={(e) => setFormData({ ...formData, certification_name: e.target.value })}
                    className="input-sovereign"
                    placeholder="e.g., PMP, PRINCE2, Six Sigma"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400">Issuing Authority</label>
                  <Input
                    value={formData.issuing_authority}
                    onChange={(e) => setFormData({ ...formData, issuing_authority: e.target.value })}
                    className="input-sovereign"
                    placeholder="e.g., PMI, Axelos"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Issue Date</label>
                    <Input
                      type="date"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Expiry Date</label>
                    <Input
                      type="date"
                      value={formData.expiry_date}
                      onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Add</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Certifications Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">All Certifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Certification</th>
                  <th>Issuing Authority</th>
                  <th>Issue Date</th>
                  <th>Expiry Date</th>
                  <th>Days to Expiry</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {certifications.map((cert) => (
                  <tr key={cert.id}>
                    <td className="text-white font-medium">{cert.employee_name}</td>
                    <td className="text-[#c9a227]">{cert.certification_name}</td>
                    <td className="text-slate-300">{cert.issuing_authority}</td>
                    <td className="text-slate-300">{cert.issue_date}</td>
                    <td className="text-slate-300">{cert.expiry_date || "No expiry"}</td>
                    <td className={cert.days_to_expiry < 0 ? "text-red-400" : cert.days_to_expiry < 30 ? "text-yellow-400" : "text-green-400"}>
                      {cert.days_to_expiry !== undefined ? `${cert.days_to_expiry} days` : "N/A"}
                    </td>
                    <td>
                      <Badge className={getStatusBadge(cert.expiry_status)}>
                        {cert.expiry_status?.replace("_", " ") || "active"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CertificationsPage;
